import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLightbulb,
  faBookOpen,
  faHeart,
  faRocket,
  faPlay,
  faLink,
  faGraduationCap,
  faCalendarCheck,
  faStar,
  faExternalLinkAlt,
  faSpinner,
  faBrain,
  faArrowLeft,
  faLayerGroup,
  faClock
} from "@fortawesome/free-solid-svg-icons";
import { faYoutube } from "@fortawesome/free-brands-svg-icons";
import Header from "../../Shared/Header";
import RafiqChatBot from "../../Shared/RafiqChatBot";
import YoutubePlayer from "../../Shared/YoutubePlayer";
import ApiServices from "../../Services/ApiServices";
import botImage from "../../../assets/images/bot.png";

const EMPTY_OPTIONS = [];

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Zakerly = () => {
  const [rafiqOpen, setRafiqOpen] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [ytOpen, setYtOpen] = useState(false);
  const [ytUrl, setYtUrl] = useState("");
  const [ytTitle, setYtTitle] = useState("");

  const buildPostResultOptions = useCallback((result) => {
    const channels = (result?.resources || []).filter((r) => r.type === "channel");
    if (channels.length === 0) return EMPTY_OPTIONS;
    return [
      { text: `📺 عرض القنوات التعليمية (${channels.length})`, value: "show_channels" },
      { text: "✏️ سؤال جديد", value: "new_question" },
    ];
  }, []);

  const handleAIMessage = useCallback(async (userInput) => {
    setIsLoadingAI(true);
    try {
      const response = await ApiServices.post("student/zakerly", JSON.stringify(userInput), false, 200000, { headers: { "Content-Type": "application/json" } });
      if (!response.isAcademic) {
        return { message: response.outOfScopeMessage || "⚠️ سؤالك خارج النطاق التعليمي. من فضلك اسألني عن مادة أو موضوع دراسي تحتاج مساعدة فيه.", nextOptions: EMPTY_OPTIONS };
      }
      setAiResult(response);
      setRafiqOpen(false);
      return { message: "✅ تم! راجع الاقتراحات والموارد أدناه.", nextOptions: buildPostResultOptions(response) };
    } catch (err) {
      return { message: "عذراً، حدث خطأ في الاتصال. حاول مرة أخرى." };
    } finally {
      setIsLoadingAI(false);
    }
  }, [buildPostResultOptions]);

  const handleOptionSelect = useCallback((option) => {
    if (option.value === "show_channels") {
      setRafiqOpen(false);
      setTimeout(() => document.getElementById("zk-channels")?.scrollIntoView({ behavior: "smooth" }), 200);
      return { message: "تم التمرير إلى قسم القنوات 👇" };
    }
    if (option.value === "new_question") {
      setAiResult(null);
      return { message: "أهلاً! أخبرني بالموضوع الذي تريد التعلم فيه 😊" };
    }
    return null;
  }, []);

  const openYoutube = (url, title) => {
    setYtUrl(url);
    setYtTitle(title);
    setYtOpen(true);
  };

  const channels = (aiResult?.resources || []).filter((r) => r.type === "channel");
  const websites = (aiResult?.resources || []).filter((r) => r.type !== "channel");

  return (
    <div className="zk-page">
      <Header />
      <br />

      <section className="zk-root" id="zakerly">
        <div className="zk-container">
          {/* ── header ── */}
          <motion.div
            className="zk-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div className="zk-eyebrow" variants={fadeUp} custom={0}>
              <span className="zk-eyebrow-dot" />
              مساعدك الشخصي للتعلم
            </motion.div>
            <motion.h2 className="zk-title" variants={fadeUp} custom={1}>
              ابدأ رحلة <em>التفوق الدراسي</em>
            </motion.h2>
            <motion.p className="zk-subtitle" variants={fadeUp} custom={2}>
              أخبرني بالموضوع الذي يشغلك، وسأرسم لك خارطة طريق متكاملة تشمل أهم المصادر والخطوات العملية
            </motion.p>
          </motion.div>

          <AnimatePresence mode="wait">
            {!aiResult ? (
              <motion.div
                key="intro"
                className="zk-intro-view"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
              >
                <div className="zk-hero-card">
                  <div className="zk-hero-bg" />
                  <div className="zk-hero-content">
                    <div className="zk-bot-avatar">
                      <motion.img
                        src={botImage}
                        alt="Rafiq"
                        animate={{ y: [0, -15, 0] }}
                        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                      />
                      <div className="zk-bot-glow" />
                    </div>
                    <div className="zk-hero-text">
                      <div className="zk-badge-ai"><FontAwesomeIcon icon={faStar} /> مدعوم بالذكاء الاصطناعي</div>
                      <h3>أهلاً بك في "ذاكرلي"</h3>
                      <p>خبيرك التعليمي الخاص متاح دائماً ليساعدك في تنظيم وقتك واختيار أفضل مصادر التعلم على يوتيوب والإنترنت.</p>
                      <div className="zk-hero-tags">
                        <div className="zk-tag"><FontAwesomeIcon icon={faGraduationCap} /> خطط دراسية</div>
                        <div className="zk-tag"><FontAwesomeIcon icon={faYoutube} /> ترشيحات يوتيوب</div>
                        <div className="zk-tag"><FontAwesomeIcon icon={faHeart} /> دعم نفسي</div>
                      </div>
                      <button className="zk-main-btn" onClick={() => setRafiqOpen(true)} disabled={isLoadingAI}>
                        {isLoadingAI ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faRocket} />}
                        <span>ابدأ التجربة الآن</span>
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="result"
                className="zk-result-view"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="zk-result-grid">
                  {/* LEFT COLUMN: Advice & Plan */}
                  <div className="zk-main-col">
                    {aiResult.encouragement && (
                      <div className="zk-card zk-encourage">
                        <div className="zk-card-icon red"><FontAwesomeIcon icon={faHeart} /></div>
                        <div className="zk-card-body">
                          <h4>رسالة دعم لك</h4>
                          <p>{aiResult.encouragement}</p>
                        </div>
                      </div>
                    )}

                    {aiResult.topicBreakdown?.length > 0 && (
                      <div className="zk-card">
                        <div className="zk-card-header">
                          <div className="zk-card-icon blue"><FontAwesomeIcon icon={faBookOpen} /></div>
                          <h4>محاور الموضوع الرئيسية</h4>
                        </div>
                        <ul className="zk-check-list">
                          {aiResult.topicBreakdown.map((t, i) => (
                            <motion.li key={i} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                              <span className="dot"><FontAwesomeIcon icon={faLayerGroup} /></span>
                              {t}
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {aiResult.studyPlan?.length > 0 && (
                      <div className="zk-card">
                        <div className="zk-card-header">
                          <div className="zk-card-icon green"><FontAwesomeIcon icon={faCalendarCheck} /></div>
                          <h4>خطتك الدراسية المقترحة</h4>
                        </div>
                        <div className="zk-timeline">
                          {aiResult.studyPlan.map((s, i) => (
                            <div key={i} className="zk-timeline-item">
                              <span className="step">{i + 1}</span>
                              <p>{s}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* RIGHT COLUMN: Resources */}
                  <div className="zk-side-col">
                    {channels.length > 0 && (
                      <div className="zk-resources-box" id="zk-channels">
                        <div className="zk-side-header">
                          <FontAwesomeIcon icon={faYoutube} />
                          <h4>قنوات يوتيوب موصى بها</h4>
                        </div>
                        <div className="zk-channels-list">
                          {channels.map((ch, i) => (
                            <motion.div key={i} className="zk-ch-card" whileHover={{ x: -5 }}>
                              <div className="zk-ch-icon"><FontAwesomeIcon icon={faYoutube} /></div>
                              <div className="zk-ch-info">
                                <h5>{ch.title}</h5>
                                <div className="zk-ch-btns">
                                  <button onClick={() => openYoutube(ch.url, ch.title)}><FontAwesomeIcon icon={faPlay} /> مشاهدة</button>
                                  <a href={ch.url} target="_blank" rel="noreferrer"><FontAwesomeIcon icon={faExternalLinkAlt} /></a>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}

                    {websites.length > 0 && (
                      <div className="zk-resources-box">
                        <div className="zk-side-header">
                          <FontAwesomeIcon icon={faLink} />
                          <h4>مواقع ومصادر أخرى</h4>
                        </div>
                        <div className="zk-links-list">
                          {websites.map((site, i) => (
                            <a key={i} href={site.url} target="_blank" rel="noreferrer" className="zk-link-item">
                              <span>{site.title}</span>
                              <FontAwesomeIcon icon={faArrowLeft} />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {aiResult.motivationalNote && (
                      <div className="zk-motivation-card">
                        <FontAwesomeIcon icon={faLightbulb} />
                        <p>{aiResult.motivationalNote}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="zk-bottom-bar">
                  <button className="zk-btn-ask" onClick={() => setRafiqOpen(true)}><FontAwesomeIcon icon={faRocket} /> سؤال جديد</button>
                  <button className="zk-btn-reset" onClick={() => setAiResult(null)}><FontAwesomeIcon icon={faSpinner} /> إعادة تهيئة</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <RafiqChatBot
        isOpen={rafiqOpen}
        onClose={() => setRafiqOpen(false)}
        isAIChat={true}
        enableChat={true}
        onAIMessage={handleAIMessage}
        onOptionSelect={handleOptionSelect}
        options={EMPTY_OPTIONS}
        welcomeMessage="أهلاً بك في ذاكرلي! 🎓 أخبرني بالمادة أو الموضوع الذي تريد التطور فيه، وسأضع لك خطة دراسية مع أفضل الموارد."
        externalLoading={isLoadingAI}
      />

      <YoutubePlayer
        isOpen={ytOpen}
        onClose={() => setYtOpen(false)}
        url={ytUrl}
        title={ytTitle}
      />

      <style>{`
        .zk-page { background: #fff; min-height: 100vh; }
        .zk-root {
          font-family: "Zain", sans-serif; direction: rtl;
          padding: 80px 0 100px; isolation: isolate;
        }
        .zk-container { max-width: 1260px; margin: 0 auto; padding: 0 40px; }

        /* Header */
        .zk-header { text-align: center; margin-bottom: 60px; }
        .zk-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          color: #6366f1; font-weight: 800; font-size: 0.82rem;
          letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 22px;
        }
        .zk-eyebrow-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
          animation: zk-pulse 2.2s ease-in-out infinite;
        }
        @keyframes zk-pulse {
          0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.45); }
          50%{ box-shadow: 0 0 0 9px rgba(232,146,10,0); }
        }
        .zk-title { font-size: clamp(2.2rem, 4vw, 3.6rem); font-weight: 900; color: #0f172a; margin: 0 0 18px; line-height: 1.2; }
        .zk-title em { font-style: normal; background: linear-gradient(90deg, #6366f1 0%, #6366f1 55%, #6366f1 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .zk-subtitle { font-size: 1.08rem; color: #64748b; line-height: 1.85; max-width: 700px; margin: 0 auto; }

        /* Hero Card */
        .zk-hero-card {
          background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 40px; padding: 60px;
          position: relative; overflow: hidden; color: #0f172a;
        }
        .zk-hero-bg {
          position: absolute; inset: 0;
          background: radial-gradient(circle at 10% 20%, rgba(99,102,241,0.05) 0%, transparent 50%),
                      radial-gradient(circle at 90% 80%, rgba(232,146,10,0.05) 0%, transparent 50%);
        }
        .zk-hero-content { position: relative; z-index: 2; display: flex; align-items: center; gap: 60px; }
        .zk-bot-avatar { position: relative; flex-shrink: 0; }
        .zk-bot-avatar img { width: 180px; height: 180px; filter: drop-shadow(0 10px 30px rgba(99,102,241,0.4)); }
        .zk-bot-glow { position: absolute; inset: 20px; background: #6366f1; filter: blur(40px); opacity: 0.3; border-radius: 50%; z-index: -1; }
        
        .zk-hero-text h3 { font-size: 2.2rem; font-weight: 900; margin-bottom: 15px; }
        .zk-hero-text p { font-size: 1.15rem; color: #94a3b8; line-height: 1.7; max-width: 500px; margin-bottom: 30px; }
        .zk-badge-ai { display: inline-flex; align-items: center; gap: 8px; color: #6366f1; font-weight: 900; font-size: 0.9rem; margin-bottom: 15px; }
        .zk-hero-tags { display: flex; gap: 15px; margin-bottom: 40px; }
        .zk-tag { background: #fff; padding: 8px 18px; border-radius: 100px; font-weight: 800; font-size: 0.85rem; display: flex; align-items: center; gap: 10px; color: #475569; border: 1px solid #e2e8f0; }
        
        .zk-main-btn { background: #6366f1; color: #fff; border: none; padding: 16px 45px; border-radius: 20px; font-weight: 900; font-family: inherit; font-size: 1.15rem; cursor: pointer; transition: 0.3s; display: flex; align-items: center; gap: 15px; box-shadow: 0 15px 30px rgba(99,102,241,0.25); }
        .zk-main-btn:hover { background: #4f46e5; transform: translateY(-3px); }

        /* Results */
        .zk-result-grid { display: grid; grid-template-columns: 1fr 340px; gap: 40px; }
        .zk-main-col { display: flex; flex-direction: column; gap: 25px; }
        .zk-side-col { display: flex; flex-direction: column; gap: 25px; }
        
        .zk-card { background: #fff; border: 1px solid #f1f5f9; border-radius: 30px; padding: 35px; box-shadow: 0 10px 30px rgba(0,0,0,0.02); }
        .zk-card-header { display: flex; align-items: center; gap: 15px; margin-bottom: 25px; }
        .zk-card-header h4 { font-size: 1.3rem; font-weight: 900; color: #0f172a; margin: 0; }
        .zk-card-icon { width: 45px; height: 45px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; flex-shrink: 0; }
        .zk-card-icon.red { background: #fef2f2; color: #ef4444; }
        .zk-card-icon.blue { background: #f0f9ff; color: #0ea5e9; }
        .zk-card-icon.green { background: #f0fdf4; color: #22c55e; }

        .zk-encourage { display: flex; gap: 20px; align-items: flex-start; background: #fffcfc; border-right: 5px solid #ef4444; }
        .zk-encourage h4 { font-size: 1.1rem; font-weight: 900; color: #ef4444; margin: 0 0 8px; }
        .zk-encourage p { color: #475569; font-weight: 800; line-height: 1.7; margin: 0; }

        .zk-check-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 15px; }
        .zk-check-list li { display: flex; align-items: flex-start; gap: 15px; font-weight: 800; color: #334155; line-height: 1.6; }
        .zk-check-list .dot { width: 30px; height: 30px; background: #f1f5f9; color: #6366f1; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 0.9rem; flex-shrink: 0; }

        .zk-timeline { position: relative; padding-right: 40px; }
        .zk-timeline::before { content: ''; position: absolute; top: 0; right: 15px; bottom: 0; width: 2px; background: #f1f5f9; }
        .zk-timeline-item { position: relative; margin-bottom: 25px; }
        .zk-timeline-item:last-child { margin-bottom: 0; }
        .zk-timeline-item .step { position: absolute; top: 0; right: -40px; width: 32px; height: 32px; background: #fff; border: 2px solid #22c55e; color: #22c55e; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 900; font-size: 0.9rem; z-index: 2; }
        .zk-timeline-item p { color: #334155; font-weight: 800; line-height: 1.6; margin: 0; }

        /* Side Column */
        .zk-resources-box { background: #f8fafc; border-radius: 24px; padding: 25px; border: 1px solid #f1f5f9; }
        .zk-side-header { display: flex; align-items: center; gap: 12px; color: #0f172a; margin-bottom: 20px; }
        .zk-side-header h4 { font-size: 1.1rem; font-weight: 900; margin: 0; }
        .zk-side-header i { color: #6366f1; }

        .zk-ch-card { background: #fff; border-radius: 16px; padding: 15px; display: flex; gap: 15px; margin-bottom: 12px; border: 1px solid #f1f5f9; }
        .zk-ch-icon { width: 40px; height: 40px; background: #fff0f0; color: #ef4444; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; flex-shrink: 0; }
        .zk-ch-info h5 { font-size: 0.9rem; font-weight: 900; color: #1e293b; margin: 0 0 10px; line-height: 1.4; }
        .zk-ch-btns { display: flex; gap: 8px; }
        .zk-ch-btns button { background: #6366f1; color: #fff; border: none; padding: 6px 12px; border-radius: 8px; font-family: inherit; font-size: 0.75rem; font-weight: 800; cursor: pointer; display: flex; align-items: center; gap: 6px; }
        .zk-ch-btns a { width: 28px; height: 28px; background: #f1f5f9; color: #64748b; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 0.8rem; }

        .zk-links-list { display: flex; flex-direction: column; gap: 10px; }
        .zk-link-item { background: #fff; padding: 12px 18px; border-radius: 12px; display: flex; justify-content: space-between; align-items: center; color: #1e293b; font-weight: 800; font-size: 0.9rem; border: 1px solid #f1f5f9; text-decoration: none; }
        .zk-link-item:hover { border-color: #6366f1; color: #6366f1; }

        .zk-motivation-card { background: #fefce8; border: 1px solid #fef08a; padding: 25px; border-radius: 24px; text-align: center; color: #854d0e; }
        .zk-motivation-card i { font-size: 1.8rem; margin-bottom: 15px; display: block; }
        .zk-motivation-card p { font-weight: 800; line-height: 1.7; margin: 0; }

        .zk-bottom-bar { display: flex; justify-content: center; gap: 20px; margin-top: 50px; }
        .zk-btn-ask { background: #6366f1; color: #fff; border: none; padding: 16px 35px; border-radius: 16px; font-weight: 900; font-family: inherit; cursor: pointer; display: flex; align-items: center; gap: 12px; }
        .zk-btn-reset { background: #f1f5f9; color: #475569; border: none; padding: 16px 35px; border-radius: 16px; font-weight: 900; font-family: inherit; cursor: pointer; display: flex; align-items: center; gap: 12px; }

        @media(max-width:1100px){
          .zk-result-grid { grid-template-columns: 1fr; }
        }
        @media(max-width:768px){
          .zk-root { padding: 60px 0 80px; }
          .zk-container { padding: 0 20px; }
          .zk-hero-card { padding: 40px; }
          .zk-hero-content { flex-direction: column; text-align: center; }
          .zk-bottom-bar { flex-direction: column; }
        }
      `}</style>
    </div>
  );
};

export default Zakerly;
